import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  BanknoteIcon,
  CalendarClockIcon,
  Building2,
  AlertTriangleIcon,
  CheckCircleIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DataListToolbar } from '@/components/data/DataListToolbar'
import {
  PersistentListCard,
  type PersistentListCardEmptyProps,
} from '@/components/data/PersistentListCard'
import {
  completePaymentCycleApi,
  getPaymentCyclesDueApi,
  type PaymentCycleDue,
  type PaymentCyclesDueResponse,
} from '@/lib/api'
import { cn } from '@/lib/utils'

const PAGE_SIZE = 20

/** Format a string dollar amount as `$1,234.56`. Uses the cycle's currency_symbol when provided. */
function formatAmount(amount: string, currencySymbol?: string | null): string {
  const n = Number(amount)
  const symbol = currencySymbol || '$'
  if (!Number.isFinite(n)) return `${symbol}${amount}`
  return `${symbol}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/** Format a YYYY-MM-DD date as a friendly label. */
function formatDueDate(iso: string): string {
  try {
    const d = new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso)
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return iso
  }
}

/** Status badge treatment for the cycle status. */
const STATUS_STYLES: Record<string, { label: string; className: string }> = {
  overdue: { label: 'Overdue', className: 'bg-destructive/10 text-destructive' },
  pending: { label: 'Pending', className: 'bg-muted text-muted-foreground' },
  paid: { label: 'Paid', className: 'bg-primary/10 text-primary' },
}

/** Status badge treatment for the parent request status. */
const REQUEST_STATUS_STYLES: Record<string, string> = {
  open: 'bg-muted text-muted-foreground',
  full: 'bg-primary/10 text-primary',
  paying: 'bg-primary/10 text-primary',
  completed: 'bg-primary/10 text-primary',
  cancelled: 'bg-destructive/10 text-destructive',
}

export const Route = createFileRoute('/dashboard/pending-payments')({
  component: () => {
    const [response, setResponse] = useState<PaymentCyclesDueResponse | null>(null)
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [fetchError, setFetchError] = useState<string | null>(null)
    const [completingIds, setCompletingIds] = useState<Set<number>>(new Set())

    const fetchData = useCallback(
      async (
        targetPage: number,
        isRefresh: boolean,
        signal?: { cancelled: boolean },
      ) => {
        if (signal?.cancelled) return
        if (isRefresh) setRefreshing(true)
        else setLoading(true)
        setFetchError(null)
        try {
          const data = await getPaymentCyclesDueApi(targetPage, PAGE_SIZE)
          if (signal?.cancelled) return
          setResponse(data)
        } catch (err) {
          if (signal?.cancelled) return
          setFetchError(
            err instanceof Error ? err.message : 'Failed to load pending payments',
          )
        } finally {
          if (!signal?.cancelled) {
            setLoading(false)
            setRefreshing(false)
          }
        }
      },
      [],
    )

    useEffect(() => {
      const signal = { cancelled: false }
      fetchData(page, false, signal)
      return () => {
        signal.cancelled = true
      }
    }, [page, fetchData])

    useEffect(() => {
      if (!response) return
      const newTotalPages = Math.max(1, Math.ceil(response.total / PAGE_SIZE))
      if (page > newTotalPages) setPage(1)
    }, [response, page])

    const cycles: PaymentCycleDue[] = response?.data ?? []
    const total = response?.total ?? 0
    const initialLoaded = response !== null || fetchError !== null
    const isInitialLoad = loading && !initialLoaded

    const handleRefresh = () => fetchData(page, true)

    /**
     * Optimistically mark a cycle complete — row fades out with a brief
     * animation, then is removed optimistically. The API call fires in parallel;
     * on failure the row stays in place. The `completingIds` set drives both
     * the exit animation and the button state ("Confirming…").
     */
    const handleConfirmPayout = useCallback(
      async (cycleId: number) => {
        if (completingIds.has(cycleId)) return

        const originalIndex = cycles.findIndex(
          (c) => c.payment_cycle_id === cycleId,
        )
        const removed = originalIndex >= 0 ? cycles[originalIndex] : null
        const originalTotal = cycles.length

        // Start exit animation immediately and fire the API call in parallel.
        setCompletingIds((prev) => {
          const next = new Set(prev)
          next.add(cycleId)
          return next
        })

        const apiPromise = completePaymentCycleApi(cycleId)

        // Wait for the animation to play (~250ms), then optimistically remove
        // the row regardless of API speed — the API response handles restoration.
        await new Promise<void>((resolve) => setTimeout(resolve, 250))
        setResponse((prev) =>
          prev
            ? {
                ...prev,
                data: prev.data.filter((c) => c.payment_cycle_id !== cycleId),
                total: Math.max(0, prev.total - 1),
              }
            : prev,
        )

        try {
          const { message } = await apiPromise
          toast.success(message || `Cycle #${cycleId} marked as paid`)
        } catch (err) {
          // API failed — restore the row
          setResponse((prev) => {
            if (!prev) return prev
            const next = [...prev.data]
            if (
              removed &&
              !next.some((c) => c.payment_cycle_id === removed.payment_cycle_id)
            ) {
              const insertAt = Math.min(originalIndex, next.length)
              next.splice(insertAt, 0, removed)
            }
            return { ...prev, data: next, total: Math.max(next.length, originalTotal) }
          })
          toast.error(
            err instanceof Error ? err.message : 'Failed to confirm payout',
          )
        } finally {
          setCompletingIds((prev) => {
            const next = new Set(prev)
            next.delete(cycleId)
            return next
          })
        }
      },
      [cycles, completingIds],
    )

    const subtitle = isInitialLoad ? (
      <span className="inline-block h-4 w-56 animate-pulse rounded bg-muted align-middle" />
    ) : total === 0 && !fetchError ? (
      <>All cycles for your company are paid up.</>
    ) : (
      <>
        <span className="font-medium text-foreground">{total}</span>{' '}
        {total === 1 ? 'cycle' : 'cycles'} due from this employer's requests
      </>
    )

    const empty: PersistentListCardEmptyProps | null = {
      icon: <BanknoteIcon className="size-5" />,
      title: 'No pending payment cycles.',
      description:
        'When a Funding Circle cycle becomes due, it appears here for your review.',
    }

    return (
      <div className="flex flex-col gap-6">
        <DataListToolbar
          title="Pending payments"
          subtitle={subtitle}
          onRefresh={handleRefresh}
          refreshing={refreshing}
          loading={loading}
        />

        <PersistentListCard<PaymentCycleDue>
          loading={loading}
          initialLoaded={initialLoaded}
          error={fetchError}
          errorTitle="Couldn't load due cycles"
          onRetry={handleRefresh}
          empty={empty}
          items={cycles}
          itemKey={(c) => c.payment_cycle_id}
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
          pageLoading={loading}
          renderItem={(cycle) => {
            const status = STATUS_STYLES[cycle.status] ?? {
              label: cycle.status,
              className: 'bg-muted text-muted-foreground',
            }
            const requestStatusClass =
              REQUEST_STATUS_STYLES[cycle.request_status] ??
              'bg-muted text-muted-foreground'
            const isCompleting = completingIds.has(cycle.payment_cycle_id)
            const isOverdue = cycle.status === 'overdue'

            return (
              <div
                className={cn(
                  'flex flex-col gap-4 rounded-xl border bg-card p-4 transition-all sm:flex-row sm:items-center sm:justify-between',
                  isOverdue
                    ? 'border-destructive/40 bg-destructive/5'
                    : 'border-border hover:bg-muted/30 hover:shadow-md',
                  isCompleting && 'scale-95 opacity-40 transition-all duration-300',
                )}
              >                  <div className="flex items-center gap-4 min-w-0">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-[var(--brand-mint)]/10 text-[var(--brand-mint)]">
                    <BanknoteIcon className="size-5" />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">
                        {cycle.title ?? `${cycle.creator_name}'s Circle`} · Cycle #{cycle.cycle_number}
                      </span>
                      <span
                        className={cn(
                          'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                          status.className,
                        )}
                      >
                        {status.label}
                      </span>
                      <span
                        className={cn(
                          'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                          requestStatusClass,
                        )}
                      >
                        {cycle.request_status.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        <CalendarClockIcon className="size-3.5" />
                        Due {formatDueDate(cycle.due_date)}
                      </span>
                      {cycle.overdue_date && cycle.status === 'overdue' && (
                        <span className="inline-flex items-center gap-1.5 text-destructive">
                          <AlertTriangleIcon className="size-3.5" />
                          Overdue since {formatDueDate(cycle.overdue_date)}
                        </span>
                      )}
                      <span>Spots: {cycle.spots_progress}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4 sm:justify-end">
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-sm font-semibold tabular-nums">
                      {formatAmount(cycle.cycle_amount_per_person, cycle.currency_symbol)}
                    </span>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {formatAmount(cycle.total_expected, cycle.currency_symbol)}
                    </span>
                  </div>
                  {cycle.payable_by_me ? (
                    <Button
                      type="button"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => handleConfirmPayout(cycle.payment_cycle_id)}
                      disabled={isCompleting || cycle.status === 'paid'}
                    >
                      {isCompleting ? (
                        <>
                          <span className="inline-block size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          Confirming…
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon className="size-3.5" />
                          Confirm payout
                        </>
                      )}
                    </Button>
                  ) : (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Building2 className="size-3.5" />
                      <span>
                        Will be paid by{' '}
                        <span className="font-medium text-foreground">
                          {cycle.paying_org_name}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )
          }}
        />
      </div>
    )
  },
})
